import React, { useState, useMemo, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { usePromptPreviews } from '@/hooks/usePromptPreviews';
import { useKeydates } from '@/hooks/useKeydates';
import { useScheduledPostsWithPrompts } from '@/hooks/useScheduledPostsWithPrompts';
import { useBrandManager } from '@/hooks/useBrandManager';
import { useIsMobile } from '@/hooks/use-mobile';
import { Loader2, Search, Calendar, FileText, X, Sparkles, Crown, ChevronLeft, ChevronRight } from 'lucide-react';

// Helper function to check if URL is a video
const isVideoUrl = (url: string): boolean => {
  const videoExtensions = ['.mp4', '.webm', '.ogg', '.mov', '.avi', '.mkv'];
  return videoExtensions.some(ext => url.toLowerCase().includes(ext));
};

interface PromptPreviewCarouselProps {
  onPromptSelect?: (promptId: string, promptText: string, promptName: string) => void;
  selectedPromptId?: string | null;
  selectedGenerationType?: string;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
}

const tabButtons = [
  { id: 'search', label: 'Search', icon: Search },
  { id: 'keydates', label: 'Key Dates', icon: Calendar },
  { id: 'articles', label: 'Articles', icon: FileText }
];

export function PromptPreviewCarousel({ 
  onPromptSelect,
  selectedPromptId,
  selectedGenerationType,
  activeTab: externalActiveTab,
  onTabChange
}: PromptPreviewCarouselProps) {
  const { prompts, isLoading, error } = usePromptPreviews();
  const { currentBrandId } = useBrandManager();
  const { items: keydates, isLoading: keydatesLoading } = useKeydates(currentBrandId);
  const { posts: scheduledPosts, isLoading: articlesLoading, generatePromptWithAI } = useScheduledPostsWithPrompts(currentBrandId);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [internalActiveTab, setInternalActiveTab] = useState('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [keydateIndex, setKeydateIndex] = useState(0);
  const [articleIndex, setArticleIndex] = useState(0);

  // Use external activeTab if provided, otherwise use internal state
  const activeTab = externalActiveTab || internalActiveTab;
  const setActiveTab = (tab: string) => {
    if (onTabChange) {
      onTabChange(tab);
    } else {
      setInternalActiveTab(tab);
    }
  };
  
  const isMobile = useIsMobile();

  // Generate consistent colors for keydates and articles
  const generateItemColor = (index: number) => {
    const colors = [
      'bg-gradient-to-br from-blue-500 to-blue-600',
      'bg-gradient-to-br from-green-500 to-green-600',
      'bg-gradient-to-br from-purple-500 to-purple-600',
      'bg-gradient-to-br from-orange-500 to-orange-600',
      'bg-gradient-to-br from-pink-500 to-pink-600',
      'bg-gradient-to-br from-teal-500 to-teal-600',
      'bg-gradient-to-br from-red-500 to-red-600',
      'bg-gradient-to-br from-indigo-500 to-indigo-600',
      'bg-gradient-to-br from-yellow-500 to-yellow-600',
      'bg-gradient-to-br from-cyan-500 to-cyan-600'
    ];
    return colors[index % colors.length];
  };

  const generatePromptColor = (index: number) => {
    const colors = [
      'bg-gradient-to-br from-slate-400 to-slate-500',
      'bg-gradient-to-br from-gray-400 to-gray-500',
      'bg-gradient-to-br from-zinc-400 to-zinc-500',
      'bg-gradient-to-br from-neutral-400 to-neutral-500',
      'bg-gradient-to-br from-stone-400 to-stone-500'
    ];
    return colors[index % colors.length];
  };

  // Filter prompts based on search query and generation type
  const filteredPrompts = useMemo(() => {
    let filtered = prompts;
    
    // Filter by generation type first
    if (selectedGenerationType && selectedGenerationType !== 'all') {
      filtered = filtered.filter(prompt => 
        prompt.type?.toLowerCase() === selectedGenerationType.toLowerCase()
      );
    }
    
    // Then filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(prompt => 
        prompt.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        prompt.prompt?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        prompt.type?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    return filtered;
  }, [prompts, searchQuery, selectedGenerationType]);

  // Reset current index when search results change
  React.useEffect(() => {
    if (filteredPrompts.length > 0 && currentIndex >= filteredPrompts.length) {
      setCurrentIndex(0);
    }
  }, [filteredPrompts.length, currentIndex]);

  // Keyboard navigation
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check if user is currently typing in an input/textarea
      const activeElement = document.activeElement;
      const isTyping = activeElement?.tagName === 'INPUT' || 
                       activeElement?.tagName === 'TEXTAREA' ||
                       activeElement?.getAttribute('contenteditable') === 'true';
      
      // Only handle arrow keys if user is NOT typing
      if (isTyping) {
        return; // Let the browser handle text navigation
      }
      
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        if (activeTab === 'search' && filteredPrompts.length > 0) {
          goToPrevious();
        } else if (activeTab === 'keydates' && keydates.length > 0) {
          setKeydateIndex((prev) => (prev - 1 + keydates.length) % keydates.length);
        } else if (activeTab === 'articles' && scheduledPosts.length > 0) {
          setArticleIndex((prev) => (prev - 1 + scheduledPosts.length) % scheduledPosts.length);
        }
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        if (activeTab === 'search' && filteredPrompts.length > 0) {
          goToNext();
        } else if (activeTab === 'keydates' && keydates.length > 0) {
          setKeydateIndex((prev) => (prev + 1) % keydates.length);
        } else if (activeTab === 'articles' && scheduledPosts.length > 0) {
          setArticleIndex((prev) => (prev + 1) % scheduledPosts.length);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTab, keydates.length, scheduledPosts.length, filteredPrompts.length]);

  // Reset carousel index when generation type changes to prevent stacking
  React.useEffect(() => {
    setCurrentIndex(0);
  }, [selectedGenerationType]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
        <span className="ml-2 text-sm text-muted-foreground">Loading previews...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8 text-sm text-muted-foreground">
        Error loading previews: {error}
      </div>
    );
  }

  if (prompts.length === 0) {
    return (
      <div className="text-center py-8 text-sm text-muted-foreground">
        No prompt previews available yet. Generate some images and set them as previews to see them here.
      </div>
    );
  }

  const handlePromptSelect = (prompt: any) => {
    if (onPromptSelect) {
      onPromptSelect(prompt.id, prompt.prompt, prompt.name || 'Untitled');
    }
  };

  const getCardTransform = (index: number) => {
    const offset = index - currentIndex;
    const totalCards = filteredPrompts.length;
    
    // Handle circular positioning
    let adjustedOffset = offset;
    if (offset > totalCards / 2) {
      adjustedOffset = offset - totalCards;
    } else if (offset < -totalCards / 2) {
      adjustedOffset = offset + totalCards;
    }

    // Position calculations based on the working example
    const transforms = {
      0: { x: 0, y: 0, z: 0, rotate: 0, scale: 1, zIndex: 3 },           // Center
      1: { x: 173.864, y: 60, z: -100, rotate: 10, scale: 0.85, zIndex: 2 },  // Right adjacent
      2: { x: 319.534, y: 120, z: -200, rotate: 20, scale: 0.7, zIndex: 1 },  // Far right
      [-1]: { x: -173.864, y: 60, z: -100, rotate: -10, scale: 0.85, zIndex: 2 }, // Left adjacent
      [-2]: { x: -319.534, y: 120, z: -200, rotate: -20, scale: 0.7, zIndex: 1 }, // Far left
    };

    const transform = transforms[adjustedOffset] || { x: 0, y: 200, z: -300, rotate: 0, scale: 0.3, zIndex: 0 };
    
    return {
      transform: `translateX(${transform.x}px) translateY(${transform.y}px) translateZ(${transform.z}px) rotateZ(${transform.rotate}deg) scale(${transform.scale})`,
      zIndex: transform.zIndex,
      opacity: Math.abs(adjustedOffset) > 2 ? 0 : 1
    };
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + filteredPrompts.length) % filteredPrompts.length);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % filteredPrompts.length);
  };

  const goToCard = (index: number) => {
    setCurrentIndex(index);
  };

  // Keydate navigation functions
  const goToKeydateCard = (index: number) => {
    setKeydateIndex(index);
  };

  // Article navigation functions
  const goToArticleCard = (index: number) => {
    setArticleIndex(index);
  };

  const getKeydateCardTransform = (index: number) => {
    const offset = index - keydateIndex;
    const totalCards = keydates.length;
    
    // Handle circular positioning
    let adjustedOffset = offset;
    if (offset > totalCards / 2) {
      adjustedOffset = offset - totalCards;
    } else if (offset < -totalCards / 2) {
      adjustedOffset = offset + totalCards;
    }

    // Position calculations
    const transforms = {
      0: { x: 0, y: 0, z: 0, rotate: 0, scale: 1, zIndex: 3 },
      1: { x: 173.864, y: 60, z: -100, rotate: 10, scale: 0.85, zIndex: 2 },
      2: { x: 319.534, y: 120, z: -200, rotate: 20, scale: 0.7, zIndex: 1 },
      [-1]: { x: -173.864, y: 60, z: -100, rotate: -10, scale: 0.85, zIndex: 2 },
      [-2]: { x: -319.534, y: 120, z: -200, rotate: -20, scale: 0.7, zIndex: 1 },
    };

    const transform = transforms[adjustedOffset] || { x: 0, y: 200, z: -300, rotate: 0, scale: 0.3, zIndex: 0 };
    
    return {
      transform: `translateX(${transform.x}px) translateY(${transform.y}px) translateZ(${transform.z}px) rotateZ(${transform.rotate}deg) scale(${transform.scale})`,
      zIndex: transform.zIndex,
      opacity: Math.abs(adjustedOffset) > 2 ? 0 : 1
    };
  };

  const getArticleCardTransform = (index: number) => {
    const offset = index - articleIndex;
    const totalCards = scheduledPosts.length;
    
    // Handle circular positioning
    let adjustedOffset = offset;
    if (offset > totalCards / 2) {
      adjustedOffset = offset - totalCards;
    } else if (offset < -totalCards / 2) {
      adjustedOffset = offset + totalCards;
    }

    // Position calculations
    const transforms = {
      0: { x: 0, y: 0, z: 0, rotate: 0, scale: 1, zIndex: 3 },
      1: { x: 173.864, y: 60, z: -100, rotate: 10, scale: 0.85, zIndex: 2 },
      2: { x: 319.534, y: 120, z: -200, rotate: 20, scale: 0.7, zIndex: 1 },
      [-1]: { x: -173.864, y: 60, z: -100, rotate: -10, scale: 0.85, zIndex: 2 },
      [-2]: { x: -319.534, y: 120, z: -200, rotate: -20, scale: 0.7, zIndex: 1 },
    };

    const transform = transforms[adjustedOffset] || { x: 0, y: 200, z: -300, rotate: 0, scale: 0.3, zIndex: 0 };
    
    return {
      transform: `translateX(${transform.x}px) translateY(${transform.y}px) translateZ(${transform.z}px) rotateZ(${transform.rotate}deg) scale(${transform.scale})`,
      zIndex: transform.zIndex,
      opacity: Math.abs(adjustedOffset) > 2 ? 0 : 1
    };
  };


  return (
    <div className="flex gap-4">
      {/* Vertical Controls - Hidden on mobile */}
      {!isMobile && (
        <div className="flex flex-col gap-2">
          {/* Search Input Field */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search prompts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10 h-12 w-48"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-1 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0"
                onClick={() => setSearchQuery('')}
              >
                <X className="h-3 w-3" />
              </Button>
            )}
          </div>

          {/* Prompt Library Button */}
          <Button
            variant={activeTab === 'search' ? "default" : "outline"}
            size="sm"
            className="w-48 h-10 flex items-center gap-2 justify-start"
            onClick={() => setActiveTab('search')}
          >
            <FileText className="h-4 w-4" />
            <span className="font-medium">Prompt Library</span>
          </Button>

          {/* Tab Buttons - Only show if data is available */}
          {tabButtons.slice(1).map((tab) => {
            const Icon = tab.icon;
            
            // Hide buttons when no data is available
            if (tab.id === 'keydates' && (!keydates || keydates.length === 0)) {
              return null;
            }
            if (tab.id === 'articles' && (!scheduledPosts || scheduledPosts.length === 0)) {
              return null;
            }
            
            return (
              <Button
                key={tab.id}
                variant={activeTab === tab.id ? "default" : "outline"}
                size="sm"
                className="w-48 h-10 flex items-center gap-2 justify-start"
                onClick={() => setActiveTab(tab.id)}
              >
                <Icon className="h-4 w-4" />
                <span className="font-medium">{tab.label}</span>
              </Button>
            );
          })}
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsContent value="search" className="space-y-4">
            <div className="flex flex-col items-center">
              {searchQuery && (
                <h4 className="text-sm font-medium mb-2">
                  Search Results for "{searchQuery}"
                </h4>
              )}
              <div className="flex justify-center w-full">
                <Badge variant="secondary" className="text-xs">
                  {filteredPrompts.length} {searchQuery ? 'results' : 'prompts'}
                </Badge>
              </div>
            </div>
            
            {filteredPrompts.length === 0 && searchQuery ? (
              <div className="text-center py-8 text-sm text-muted-foreground animate-fade-in">
                No prompts found matching "{searchQuery}"
              </div>
            ) : (
              <div className="mt-8 w-full h-[250px] md:h-[360px] animate-fade-in">
                <div className="relative w-full h-full overflow-hidden" style={{ touchAction: 'pan-y' }}>
                  <div 
                    key={`deck-${selectedGenerationType}`}
                    className="relative w-full h-full flex items-start justify-center" 
                    style={{ perspective: '1000px' }}
                  >
                    {filteredPrompts.map((prompt, index) => {
                      const isActive = index === currentIndex;
                      const cardStyle = getCardTransform(index);
                      const cardSize = 187.961; // Match the working example

                      return (
                        <div
                          key={`${prompt.isTemplate ? 'template' : 'brand'}-${prompt.id}`}
                          className="absolute cursor-pointer animate-scale-in"
                          style={{
                            ...cardStyle,
                            transition: '0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                            width: `${cardSize}px`,
                            height: `${cardSize}px`,
                          }}
                          onClick={() => !isActive && goToCard(index)}
                        >
                        <div className="w-full h-full rounded-xl overflow-hidden shadow-lg bg-white p-1">
                          <div className="relative group w-full h-full">
                            {prompt.preview ? (
                              isVideoUrl(prompt.preview) ? (
                                <video
                                  draggable={false}
                                  src={prompt.preview}
                                  className="w-full h-full rounded-xl object-cover"
                                  autoPlay
                                  loop
                                  muted
                                  playsInline
                                />
                              ) : (
                                <img
                                  draggable={false}
                                  src={prompt.preview}
                                  alt={prompt.name || 'Prompt preview'}
                                  className="w-full transition-transform duration-1000 ease-out hover:scale-110 w-full h-full rounded-xl"
                                />
                              )
                            ) : (
                              <div className={`w-full h-full rounded-xl ${generatePromptColor(index)} flex flex-col items-center justify-center text-white p-4`}>
                                <FileText className="h-8 w-8 mb-2 opacity-80" />
                                <div className="text-center">
                                  <div className="text-sm font-medium mb-1">
                                    {prompt.type || 'Prompt'}
                                  </div>
                                  <div className="text-xs opacity-90">
                                    No preview
                                  </div>
                                </div>
                              </div>
                            )}
                              
                              {/* Button overlay - only show on hover and active card */}
                              <div 
                                className={`absolute w-full bottom-2 justify-center flex transition-opacity duration-500 ${
                                  isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                                }`}
                              >
                                <Button
                                  size="sm"
                                  className="cursor-pointer shrink-0 inline-flex justify-center items-center rounded-[40px] overflow-hidden bg-[#0B0B0F] text-white hover:bg-[#1a1a1f] border-0"
                                  style={{ 
                                    padding: '5.37031px 21.4812px', 
                                    gap: '5.37031px',
                                    fontSize: '10.7406px',
                                    lineHeight: '16.1109px'
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (isActive) {
                                      handlePromptSelect(prompt);
                                    }
                                  }}
                                  disabled={!isActive}
                                >
                                  <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="13.42578125" 
                                    height="12.7544921875" 
                                    viewBox="0 0 20 19" 
                                    fill="none"
                                    className="mr-1"
                                  >
                                    <path d="M14.558 12.0954L15.2579 13.6369C15.4115 13.9757 15.6826 14.2473 16.021 14.4016L17.2395 14.9554C17.2894 14.9779 17.3325 15.0133 17.3642 15.058C17.3958 15.1027 17.415 15.155 17.4197 15.2096C17.4244 15.2641 17.4145 15.319 17.3909 15.3684C17.3673 15.4178 17.331 15.4601 17.2856 15.4908L15.9487 16.4016C15.6638 16.5959 15.4525 16.8804 15.3487 17.2093L15.2641 17.4708C14.9503 18.3846 14.6287 18.84 14.2979 18.84C13.9364 18.84 13.5564 18.2862 13.1595 17.1816C13.0466 16.8681 12.8349 16.5998 12.5564 16.4169L11.2103 15.54C11.1664 15.5114 11.1305 15.472 11.1061 15.4257C11.0817 15.3793 11.0695 15.3275 11.0707 15.2751C11.072 15.2227 11.0866 15.1715 11.1131 15.1263C11.1397 15.0811 11.1774 15.0435 11.2226 15.0169L12.6226 14.1954C12.8689 14.0512 13.0702 13.8413 13.2041 13.5893L14.0041 12.0785C14.0307 12.0271 14.0713 11.9843 14.1211 11.9549C14.1709 11.9256 14.228 11.9108 14.2859 11.9125C14.3437 11.9141 14.3999 11.9319 14.448 11.964C14.4961 11.9961 14.5342 12.0411 14.558 12.0939V12.0954ZM11.501 2.61849L13.1472 9.63541C13.2426 10.0508 12.8579 10.7831 12.5549 10.94C12.2518 11.0985 11.8103 10.7954 11.7149 10.3816L10.001 2.96618C9.97836 2.86766 9.93649 2.77457 9.87781 2.69224C9.81914 2.60991 9.74481 2.53996 9.65908 2.48637C9.57335 2.43279 9.4779 2.39663 9.37818 2.37997C9.27847 2.36331 9.17644 2.36646 9.07795 2.38926L3.08256 3.77387C2.98404 3.79654 2.89095 3.83841 2.80863 3.89709C2.7263 3.95576 2.65634 4.03009 2.60276 4.11582C2.54918 4.20155 2.51302 4.297 2.49636 4.39672C2.47969 4.49643 2.48285 4.59845 2.50564 4.69695L4.92872 15.1893C4.95139 15.2878 4.99326 15.3809 5.05193 15.4632C5.11061 15.5455 5.18494 15.6155 5.27067 15.6691C5.3564 15.7226 5.45185 15.7588 5.55156 15.7755C5.65128 15.7921 5.7533 15.789 5.8518 15.7662L7.72872 15.3293C8.14256 15.2339 8.54564 15.7723 8.67795 16.04C8.80872 16.3093 8.83795 16.6554 8.4241 16.7508L6.19641 17.2646C5.60008 17.4023 4.97348 17.2975 4.45444 16.9732C3.9354 16.6489 3.56644 16.1317 3.42872 15.5354L1.00872 5.0431C0.940313 4.74772 0.930786 4.44176 0.98068 4.1427C1.03057 3.84364 1.13891 3.55734 1.2995 3.30016C1.46009 3.04299 1.66979 2.81998 1.91661 2.64388C2.16342 2.46779 2.44252 2.34206 2.73795 2.27387L8.73333 0.889256C9.02882 0.820873 9.33488 0.811407 9.63403 0.861399C9.93317 0.911392 10.2195 1.01986 10.4767 1.18061C10.7339 1.34135 10.9569 1.55122 11.1329 1.79821C11.3089 2.04519 11.4346 2.32446 11.5026 2.62003L11.501 2.61849ZM14.5549 3.70156L17.8364 4.53541C18.4298 4.68465 18.9397 5.06345 19.2538 5.58852C19.568 6.11359 19.6608 6.74194 19.5118 7.33541C19.4949 7.40156 19.4949 7.40156 19.4749 7.46618L18.6241 10.1616C18.5972 10.2618 18.5502 10.3555 18.4861 10.4371C18.422 10.5187 18.3421 10.5864 18.2511 10.6363C18.1601 10.6862 18.16 10.7172 17.9568 10.7274C17.8535 10.7375 17.7492 10.7267 17.6503 10.6956C17.5513 10.6644 17.4597 10.6136 17.3809 10.5461C17.3021 10.4786 17.2377 10.3958 17.1917 10.3028C17.1457 10.2098 17.119 10.1085 17.1132 10.0049C17.1074 9.90129 17.1226 9.79759 17.1579 9.70003L18.0072 7.00464L18.0195 6.96003C18.0442 6.86204 18.0493 6.76015 18.0346 6.66018C18.0199 6.56022 17.9856 6.46413 17.9337 6.37741C17.8819 6.29068 17.8134 6.21503 17.7323 6.15477C17.6512 6.0945 17.559 6.05081 17.461 6.02618L14.878 5.33849C14.6558 5.28918 14.4515 5.17992 14.2871 5.02261C14.1227 4.86529 14.0046 4.66592 13.9456 4.44618C13.8549 4.1108 14.1441 3.59695 14.5549 3.70156Z" fill="white"/>
                                  </svg>
                                  <span className="font-medium">
                                    {selectedPromptId === prompt.id ? 'Selected' : 'Use same prompt'}
                                  </span>
                                </Button>
                              </div>
                              
                              {/* Label */}
                              <div 
                                className="absolute bg-black/35 rounded-md text-white font-semibold flex items-center gap-1"
                                style={{ 
                                  left: '16px', 
                                  top: '16px', 
                                  filter: 'drop-shadow(rgba(0, 0, 0, 0.45) 0px 4px 4px)', 
                                  padding: '2.68516px 5.37031px', 
                                  fontSize: '10.7406px', 
                                  lineHeight: '16.1109px' 
                                }}
                              >
                                {prompt.isTemplate && (
                                  <Crown className="h-3 w-3 text-amber-300" />
                                )}
                                {prompt.name || 'Untitled'}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Navigation arrows */}
                {filteredPrompts.length > 1 && (
                  <div className="relative z-10 flex justify-center items-center gap-4 -mt-8 mb-4 md:-mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 rounded-full p-0"
                      onClick={goToPrevious}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      {currentIndex + 1} of {filteredPrompts.length}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 rounded-full p-0"
                      onClick={goToNext}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="keydates" className="space-y-4">
            {keydatesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span className="ml-2 text-sm text-muted-foreground">Loading key dates...</span>
              </div>
            ) : keydates.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">
                No key dates available yet. Add some key dates to see them here.
              </div>
            ) : (
              <>
                <div className="flex justify-center w-full">
                  <Badge variant="secondary" className="text-xs">
                    {keydates.length} dates
                  </Badge>
                </div>
                
                <div className="mt-8 w-full h-[250px] md:h-[360px] animate-fade-in">
                  <div className="relative w-full h-full overflow-hidden" style={{ touchAction: 'pan-y' }}>
                    <div 
                      key={`deck-keydates`}
                      className="relative w-full h-full flex items-start justify-center" 
                      style={{ perspective: '1000px' }}
                    >
                      {keydates.map((keydate, index) => {
                        const isActive = index === keydateIndex;
                        const cardStyle = getKeydateCardTransform(index);
                        const cardSize = 187.961;
                        const colorClass = generateItemColor(index);

                        return (
                          <div
                            key={`keydate-${keydate.id}`}
                            className="absolute cursor-pointer animate-scale-in"
                            style={{
                              ...cardStyle,
                              transition: '0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                              width: `${cardSize}px`,
                              height: `${cardSize}px`,
                            }}
                            onClick={() => !isActive && goToKeydateCard(index)}
                          >
                            <div className="w-full h-full rounded-xl overflow-hidden shadow-lg bg-white p-1">
                              <div className="relative group w-full h-full">
                                <div className={`w-full h-full rounded-xl ${colorClass} flex flex-col items-center justify-center text-white p-4`}>
                                  <Calendar className="h-8 w-8 mb-2 opacity-80" />
                                  <div className="text-center">
                                    <div className="text-lg font-bold mb-1">
                                      {new Date(keydate.date).getDate()}
                                    </div>
                                    <div className="text-sm opacity-90">
                                      {new Date(keydate.date).toLocaleDateString('en-US', { 
                                        month: 'short',
                                        year: 'numeric'
                                      })}
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Button overlay - only show on hover and active card */}
                                <div 
                                  className={`absolute w-full bottom-2 justify-center flex transition-opacity duration-500 ${
                                    isActive ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                                  }`}
                                >
                                  <Button
                                    size="sm"
                                    className="cursor-pointer shrink-0 inline-flex justify-center items-center rounded-[40px] overflow-hidden bg-[#0B0B0F] text-white hover:bg-[#1a1a1f] border-0"
                                    style={{ 
                                      padding: '5.37031px 21.4812px', 
                                      gap: '5.37031px',
                                      fontSize: '10.7406px',
                                      lineHeight: '16.1109px'
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (isActive && onPromptSelect) {
                                        onPromptSelect(keydate.id, keydate.keydate_prompt || '', keydate.name || 'Key Date');
                                      }
                                    }}
                                    disabled={!isActive}
                                  >
                                    <Calendar className="h-3 w-3 mr-1" />
                                    <span className="font-medium">
                                      Use this date
                                    </span>
                                  </Button>
                                </div>
                                
                                {/* Label */}
                                <div 
                                  className="absolute bg-black/35 rounded-md text-white font-semibold"
                                  style={{ 
                                    left: '16px', 
                                    top: '16px', 
                                    filter: 'drop-shadow(rgba(0, 0, 0, 0.45) 0px 4px 4px)', 
                                    padding: '2.68516px 5.37031px', 
                                    fontSize: '10.7406px', 
                                    lineHeight: '16.1109px' 
                                  }}
                                >
                                  {keydate.name || keydate.description || 'Key Date'}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                
                {/* Navigation arrows for keydates */}
                {keydates.length > 1 && (
                  <div className="relative z-10 flex justify-center items-center gap-4 -mt-8 mb-4 md:mt-6">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 rounded-full p-0"
                      onClick={() => setKeydateIndex((prev) => (prev - 1 + keydates.length) % keydates.length)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      {keydateIndex + 1} of {keydates.length}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 rounded-full p-0"
                      onClick={() => setKeydateIndex((prev) => (prev + 1) % keydates.length)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          <TabsContent value="articles" className="space-y-4">
            {articlesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span className="ml-2 text-sm text-muted-foreground">Loading articles...</span>
              </div>
            ) : scheduledPosts.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">
                No scheduled posts available. Create some scheduled posts to see them here.
              </div>
            ) : (
              <>
                <div className="flex justify-center w-full">
                  <Badge variant="secondary" className="text-xs">
                    {scheduledPosts.length} posts
                  </Badge>
                </div>
                
                <div className="mt-8 w-full h-[250px] md:h-[360px] animate-fade-in">
                  <div className="relative w-full h-full overflow-hidden" style={{ touchAction: 'pan-y' }}>
                    <div 
                      key={`deck-articles`}
                      className="relative w-full h-full flex items-start justify-center" 
                      style={{ perspective: '1000px' }}
                    >
                      {scheduledPosts.map((post, index) => {
                        const isActive = index === articleIndex;
                        const cardStyle = getArticleCardTransform(index);
                        const cardSize = 187.961;
                        const colorClass = generateItemColor(index);

                        return (
                          <div
                            key={`article-${post.id}`}
                            className="absolute cursor-pointer animate-scale-in"
                            style={{
                              ...cardStyle,
                              transition: '0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                              width: `${cardSize}px`,
                              height: `${cardSize}px`,
                            }}
                            onClick={() => !isActive && goToArticleCard(index)}
                          >
                            <div className="w-full h-full rounded-xl overflow-hidden shadow-lg bg-white p-1">
                              <div className="relative group w-full h-full">
                                <div className={`w-full h-full rounded-xl ${colorClass} flex flex-col items-center justify-center text-white p-4`}>
                                  <FileText className="h-8 w-8 mb-2 opacity-80" />
                                  <div className="text-center">
                                    <div className="text-sm font-bold mb-1 line-clamp-2">
                                      {post.post_content.substring(0, 50)}...
                                    </div>
                                    <div className="text-xs opacity-90">
                                      {post.channel} Post
                                    </div>
                                  </div>
                                </div>
                                
                                
                                {/* Prompt badges at bottom */}
                                <div className="absolute bottom-2 left-2 right-2 flex flex-wrap gap-1 justify-center">
                                  {post.prompts.length > 0 ? (
                                    post.prompts.slice(0, 3).map((prompt) => (
                                      <Badge 
                                        key={prompt.id}
                                        variant="secondary" 
                                        className="text-xs px-2 py-1 h-5 bg-white/90 text-gray-800 border-0 cursor-pointer hover:bg-white transition-colors"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (onPromptSelect) {
                                            onPromptSelect(prompt.id, prompt.prompt, prompt.name);
                                          }
                                        }}
                                      >
                                        {prompt.name}
                                      </Badge>
                                    ))
                                  ) : (
                                    <Badge
                                      variant="secondary" 
                                      className="text-xs px-2 py-1 h-5 bg-white/90 text-gray-800 border-0 cursor-pointer hover:bg-white transition-colors"
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        const aiPrompt = await generatePromptWithAI(post.post_content);
                                        if (aiPrompt && onPromptSelect) {
                                          onPromptSelect(`ai-generated-${post.id}`, aiPrompt, 'AI Generated');
                                        }
                                      }}
                                    >
                                      <Sparkles className="h-3 w-3 mr-1" />
                                      AI Generate
                                    </Badge>
                                  )}
                                  {post.prompts.length > 3 && (
                                    <Badge 
                                      variant="secondary" 
                                      className="text-xs px-2 py-1 h-5 bg-white/90 text-gray-800 border-0"
                                    >
                                      +{post.prompts.length - 3}
                                    </Badge>
                                  )}
                                </div>
                                
                                {/* Label */}
                                <div 
                                  className="absolute bg-black/35 rounded-md text-white font-semibold"
                                  style={{ 
                                    left: '16px', 
                                    top: '16px', 
                                    filter: 'drop-shadow(rgba(0, 0, 0, 0.45) 0px 4px 4px)', 
                                    padding: '2.68516px 5.37031px', 
                                    fontSize: '10.7406px', 
                                    lineHeight: '16.1109px' 
                                  }}
                                >
                                  {post.channel}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                
                {/* Navigation arrows for articles */}
                {scheduledPosts.length > 1 && (
                  <div className="relative z-10 flex justify-center items-center gap-4 -mt-8 mb-4 md:mt-6">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 rounded-full p-0"
                      onClick={() => setArticleIndex((prev) => (prev - 1 + scheduledPosts.length) % scheduledPosts.length)}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-xs text-muted-foreground">
                      {articleIndex + 1} of {scheduledPosts.length}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 w-8 rounded-full p-0"
                      onClick={() => setArticleIndex((prev) => (prev + 1) % scheduledPosts.length)}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}